import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Confirm, useNotify, useRedirect } from "react-admin";

import { API, graphqlOperation } from "aws-amplify";
import { barcodeByInventoryItemByActiveByValue } from "../../../graphql/barcodes/custom-queries";
import {
  getShipmentLineItemInventoryItemMobile,
  getShipmentLineItemLookup,
  getOutboundShipmentMobile,
  shipmentLineItemByInventoryForValidation,
} from "../../../graphql/outbound/custom-queries";
import {
  getAisle,
  locationByAisleBySortKey,
} from "../../../graphql/reloadyard/custom-queries";
import {
  updateShipmentLineItemInventoryItem,
  updateShipmentLineItem,
} from "../../../graphql/outbound/custom-mutations";
import { performUpdateOutbound } from "../../../graphql/outbound/outboundCRUD";
import { getInboundShipmentSeals } from "../../../graphql/inbound/custom-queries";

import {
  CardActions,
  Button,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  Box,
  Backdrop,
  InputAdornment,
  useMediaQuery,
  CardContent,
  MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { deepOrange, grey, orange } from "@material-ui/core/colors";
import CheckBoxOutlineBlankOutlinedIcon from "@material-ui/icons/CheckBoxOutlineBlankOutlined";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";

import { isAuthorized, logError } from "../../../utils/appUtils";
import { isRailCarNumberMatch } from "../../../utils/railCarUtils";
import { applyRejectionRollback } from "../../../utils/shipmentPlacement";
import { useTrackCarSubscriptionRefresh } from "../../../utils/useTrackCarSubscriptionRefresh";
import {
  isCapacitySliderVisible,
  getLocationFullName,
  getTrackCarLocationDisplayLabel,
  getLocationViewName,
  isTrack,
  promiseSetCapacityUsed,
  isBulkLocation,
  isUnmanagedLocation,
  sortShipmentItemByLocation,
} from "../../../utils/locationUtils";
import { getUnitName } from "../../../utils/loadTypeUtils";
import {
  AclType,
  barcodeExceptions,
  barcodeExceptionsChoices,
  LocationLoadType,
  LocationType,
  NoteType,
  OutboundState,
  OutboundStatus,
  SealStatus,
  ShipmentLineItemStatus,
} from "../../../utils/constants";

import { CustomFieldsMobile } from "../../../components/common/CustomFields";
import ErrorCard from "../../../components/ErrorCard";
import { needsOutboundRailcarSpotting } from "../../../components/common/locationPickerTrackUtils";
import {
  ExceptionIcon,
  HashIcon,
  ScannerIcon,
  SealIcon,
} from "../../../components/common/iconography";
import { ContainerNumberDialog } from "../../../components/common/ContainerNumberDialog";
import { SpeedDialList } from "../../../components/common/SpeedDialList";
import PhotoManager from "../../../components/common/PhotoManager";
import LocationCapacity, {
  StructuredLocationCapacity,
} from "../../../components/common/LocationCapacity";
import CrewCard from "../../../components/common/CrewCard";
import { accountNameRenderer } from "../../../components/common/AccountNameRenderer";

import { showWeight } from "../../../managerApp/resources/inventory/InventoryUitls";
import { TrackLocationView } from "../../../components/common/TrackLocationView";
import { ScanSealsDialog } from "./ScanSealsDialog";
import { ApplySealsDialog } from "./ApplySealsDialog";
import {
  revalidateSeal,
  breakSeal,
  createNewSeal,
  deActivateSeal,
  reActivateSeal,
  getRailcarVerificationResetState,
  getShipmentProcessingBlockedMessage,
  getShipmentSelectedTrack,
  getTrackRailcarHighlightShipmentId,
  getTrackRailcarVerificationConfig,
  shouldFetchInboundShipment,
} from "./utils";
import { useOutboundContext } from "./context";
import { BreakSealsDialog } from "./BreakSealDialog";
import { ExpectedBarcodeScanDialog } from "./ExpectedBarcodeScanDialog";

const useStyles = makeStyles((theme) => ({
  actions: {
    display: "flex",
    justifyContent: "flex-start",
  },
  progress: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 300,
  },
  location: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    flexWrap: "wrap",
    padding: theme.spacing(0.5),
    marginTop: theme.spacing(2),
  },
  bayContainer: {
    display: "flex",
    flexDirection: "column-reverse",
    alignItems: "center",
    alignContent: "space-between",
    border: "1px solid #b0bec5",
    borderRadius: theme.spacing(1),
    minWidth: theme.spacing(30),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  levelContainer: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  locationAvatar: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.getContrastText(grey[400]),
    backgroundColor: grey[400],
    borderRadius: theme.spacing(0.5),
    height: "100%",
    padding: theme.spacing(1),
    minWidth: "5rem",
  },
  locationToPickAvatar: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.getContrastText(deepOrange[500]),
    backgroundColor: deepOrange[500],
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1),
    minWidth: "5rem",
  },
  divider: {
    border: ".5px solid",
    borderColor: grey[400],
    marginBottom: theme.spacing(0.5),
    width: "100%",
  },
  backdrop: {
    zIndex: 90001,
  },
  speedDial: {
    position: "fixed",
    bottom: theme.spacing(4),
    right: theme.spacing(4),
  },
  speedDialAction: {
    width: theme.spacing(6),
    height: theme.spacing(6),
    backgroundColor: orange[400],
    "&:hover": {
      backgroundColor: orange[400],
    },
  },
}));

export const MissingScanDialog = ({ open, onClose, onConfirm }) => (
  <Confirm
    isOpen={open}
    title="Missing Scan"
    content="You have missing barcodes to scan. Do you want to add an exception
        before saving? Or cancel to continue scanning."
    onConfirm={onConfirm}
    onClose={onClose}
  />
);

const pickInventoryLocationForCrewLine = (inventoryItem, inventory) => {
  if (inventoryItem?.locationFullName) {
    return getLocationFullName(inventoryItem.locationFullName);
  }
  if (inventoryItem?.location) {
    return getLocationFullName(inventoryItem.location);
  }
  if (inventory?.locationFullName) {
    return getLocationFullName(inventory.locationFullName);
  }
  if (inventory?.location) {
    return getLocationFullName(inventory.location);
  }
  return null;
};

const InventoryItemCard = ({ history, match }) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const classes = useStyles();
  const imgRef = useRef();
  const isComponentMounted = useRef(true);
  const { state, dispatch } = useOutboundContext();
  const [isSpeedDialOpen, setIsSpeedDialOpen] = useState(false);
  const {
    currentLineItem,
    lineItemInventoryItem,
    currentShipment,
    inboundShipment,
    isSourceRailcarValidated,
    isTargetRailcarValidated,
    loading,
  } = state;
  const [isContainerNumberValidated, setIsContainerNumberValidated] =
    useState(false);
  const isContainerPickup =
    lineItemInventoryItem?.inventoryItem?.location?.type ===
    LocationType.BULK_MOBILE;
  const pickupLocation = lineItemInventoryItem?.inventoryItem?.location;
  const pickupInventory = lineItemInventoryItem?.inventoryItem?.inventory;
  const pickupContainerNumber = (
    pickupInventory?.containerNumber ||
    pickupLocation?.containerNumber ||
    ""
  ).trim();
  const [invalidIdError, setInvalidIdError] = useState();
  const [isContainerNumberDialogOpen, setIsContainerNumberDialogOpen] =
    useState(false);
  const [sourceRailcarInput, setSourceRailcarInput] = useState("");
  const [sourceRailcarError, setSourceRailcarError] = useState(false);
  const [targetRailcarInput, setTargetRailcarInput] = useState("");
  const [targetRailcarError, setTargetRailcarError] = useState(false);
  const [note, setNote] = useState();
  const [bayList, setBayList] = useState([]);
  const isMedium = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [scanSealOpen, setScanSealOpen] = useState(false);
  const [applySealOpen, setApplySealOpen] = useState(false);
  const [breakSealOpen, setBreakSealOpen] = useState(false);
  const [deactivatedSeal, setDeactivatedSeal] = useState({});
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [barcodeExceptionNote, setBarcodeExceptionNote] = useState("");
  const [isHasException, setIsHasException] = useState(false);
  const chooseBarcodeDamage = useRef(null);
  const [expectedBarcode, setExpectedBarcode] = useState([]);
  const [quantity, setQuantity] = useState(0);
  const [exceptionPromptOpen, setExceptionPromptOpen] = useState(false);
  const [totalScanned, setTotalScanned] = useState(0);
  const totalExpected = expectedBarcode.length;

  const isLoaded =
    lineItemInventoryItem?.status === ShipmentLineItemStatus.LOADED ||
    lineItemInventoryItem?.status === ShipmentLineItemStatus.VERIFIED;
  const isTrackArea = isTrack(
    lineItemInventoryItem?.inventoryItem?.location?.area,
  );
  const trackRailcarVerificationConfig = getTrackRailcarVerificationConfig({
    isTrackArea,
    inboundShipmentCarrierType: inboundShipment?.carrierType,
    verifyInboundRailCar:
      currentLineItem?.inventory?.account?.verifyInboundRailCar ||
      inboundShipment?.account?.verifyInboundRailCar,
    expectedSourceRailCarNumber:
      currentLineItem?.inventory?.railCarNumber ??
      inboundShipment?.railCarNumber,
    outboundShipmentCarrierType: currentShipment?.carrierType,
    verifyOutboundRailCar: currentShipment?.account?.verifyOutboundRailCar,
    expectedTargetRailCarNumber: currentShipment?.railCarNumber,
    isSourceRailcarValidated,
    isTargetRailcarValidated,
  });
  const trackRailcarVerificationType = trackRailcarVerificationConfig.type;
  const hideSelectedRailcar =
    trackRailcarVerificationConfig.hideSelectedRailcar;
  const inboundSelectedTrack = useMemo(
    () => getShipmentSelectedTrack({ trackCar: inboundShipment?.trackCar }),
    [inboundShipment?.trackCar],
  );
  const outboundSelectedTrack = useMemo(
    () => getShipmentSelectedTrack({ trackCar: currentShipment?.trackCar }),
    [currentShipment?.trackCar],
  );
  const selectedTrackForVerification = useMemo(() => {
    // Direction-specific track only — never fall back to the other shipment's TrackCar
    // (avoids verifying/highlighting the wrong railcar when one side lacks trackCar data).
    if (trackRailcarVerificationType === "supplying") {
      return inboundSelectedTrack;
    }

    if (trackRailcarVerificationType === "receiving") {
      return outboundSelectedTrack;
    }

    // Not blocking verification — map may fall back between shipments only outside directional modes.
    return outboundSelectedTrack ?? inboundSelectedTrack;
  }, [
    inboundSelectedTrack,
    outboundSelectedTrack,
    trackRailcarVerificationType,
  ]);
  const trackHighlightShipmentId = getTrackRailcarHighlightShipmentId({
    trackRailcarVerificationType,
    sourceShipmentId:
      inboundShipment?.id || currentLineItem?.inventory?.inboundShipmentId,
    sourceTrackId: inboundSelectedTrack?.id,
    targetShipmentId: currentShipment?.id,
    targetTrackId: outboundSelectedTrack?.id,
    selectedTrackId: selectedTrackForVerification?.id,
    isSourceRailcarValidated,
  });
  /**
   * Crew line summary: visibility from TrackCar display label or inventory full names / structured ids;
   * display priority: TrackCar-derived label then inventory fallbacks (replaces legacy shipment.location).
   */
  const { crewLocationRowVisible, crewLocationRowLabel } = useMemo(() => {
    const inventoryItem = lineItemInventoryItem?.inventoryItem;
    const inventory = currentLineItem?.inventory;
    const trackCar = currentShipment?.trackCar;
    const trackLabel = getTrackCarLocationDisplayLabel(trackCar);
    const label =
      trackLabel || pickInventoryLocationForCrewLine(inventoryItem, inventory);

    const visible =
      Boolean(trackLabel) ||
      Boolean(inventoryItem?.locationFullName) ||
      Boolean(inventory?.locationFullName) ||
      Boolean(inventoryItem?.location?.id) ||
      Boolean(inventory?.location?.id);

    return { crewLocationRowVisible: visible, crewLocationRowLabel: label };
  }, [
    currentShipment?.trackCar,
    lineItemInventoryItem?.inventoryItem,
    currentLineItem?.inventory,
  ]);

  // Quantity validation for barcode-required flows
  const isQuantityRequiredError = useMemo(() => {
    if (!expectedBarcode?.length || isLoaded) return false;
    return quantity == null || quantity === "" || Number(quantity) < 0;
  }, [expectedBarcode, isLoaded, quantity]);

  const inventoryItemLocationFullNameLabel = useMemo(() => {
    const inventoryItem = lineItemInventoryItem?.inventoryItem;
    if (!inventoryItem) return "Unspotted";
    if (inventoryItem.location) {
      return getLocationFullName(inventoryItem.location);
    }
    if (
      inventoryItem.locationFullName &&
      !inventoryItem.locationFullName.includes("-") &&
      !isNaN(inventoryItem.locationFullName)
    ) {
      return `T-${inventoryItem.locationFullName}`;
    }
    return inventoryItem.locationFullName || "Unspotted";
  }, [lineItemInventoryItem]);

  const isSaveButtonDisabled = () => {
    // Require a positive quantity when barcodes are expected and item not yet loaded
    if (!!expectedBarcode?.length && !isLoaded) {
      if (!quantity || Number(quantity) <= 0) return true;
    }
    return (
      (barcodeExceptionNote?.trim() === "" &&
        barcodeExceptionNote !== barcodeExceptions.MISMATCH) ||
      lineItemInventoryItem?.status === ShipmentLineItemStatus.VERIFIED
    );
  };

  const exceptionInputChoices = [
    { id: "", value: "", name: <span>&nbsp;</span> },
    ...barcodeExceptionsChoices,
  ];

  const seals =
    inboundShipment?.seals?.items.filter(
      (s) => s.active && !s.brokenAt && !s.brokenBy,
    ) || [];
  const isValidating = seals.some((s) => s.status !== "new" && !s.reValidated);

  const handleSpeedDialClose = () => setIsSpeedDialOpen(false);

  const handleValidateSourceRailcar = () => {
    const expectedRailCarNumber =
      currentLineItem?.inventory?.railCarNumber ??
      inboundShipment?.railCarNumber ??
      "";
    if (isRailCarNumberMatch(expectedRailCarNumber, sourceRailcarInput)) {
      dispatch({ type: "SET_SOURCE_RAILCAR_VALIDATED", payload: true });
      setSourceRailcarError(false);
      notify("Supplying railcar validated successfully.");
      return;
    }
    setSourceRailcarError(true);
    notify(
      "Railcar number does not match. Enter the correct supplying railcar number.",
      "warning",
    );
  };

  const handleValidateTargetRailcar = () => {
    if (
      isRailCarNumberMatch(
        currentShipment?.railCarNumber ?? "",
        targetRailcarInput,
      )
    ) {
      dispatch({ type: "SET_TARGET_RAILCAR_VALIDATED", payload: true });
      setTargetRailcarError(false);
      notify("Receiving railcar validated successfully.");
      return;
    }

    setTargetRailcarError(true);
    notify(
      "Railcar number does not match. Enter the correct receiving railcar number.",
      "warning",
    );
  };

  const handleSpeedDialOpen = (evt, reason) => {
    if (reason === "mouseEnter" || reason === "focus") return;
    setIsSpeedDialOpen(true);
  };

  const closeApplySealsDialog = () => {
    setApplySealOpen(false);
    setScanSealOpen(true);
  };

  const closeBreakSealsDialog = () => {
    setBreakSealOpen(false);
    setScanSealOpen(true);
  };

  const closeScanSealsDialog = () => {
    setScanSealOpen(false);
  };

  const getData = useCallback(
    async (id, { showSpinner = true } = {}) => {
      try {
        if (showSpinner) {
          dispatch({ type: "SET_LOADING", payload: true });
        }
        let response = await API.graphql(
          graphqlOperation(getShipmentLineItemInventoryItemMobile, { id }),
        );
        const lineItemInventoryItem =
          response.data.getShipmentLineItemInventoryItem;
        response = await API.graphql(
          graphqlOperation(getShipmentLineItemLookup, {
            id: lineItemInventoryItem.shipmentLineItemId,
          }),
        );

        const currentLineItem = response.data.getShipmentLineItem;
        if (currentLineItem) {
          // ALWAYS fetch the latest shipment data to ensure location name is updated
          const shipmentResponse = await API.graphql(
            graphqlOperation(getOutboundShipmentMobile, {
              id: currentLineItem.outboundShipmentId,
            }),
          );
          const inventoryItems = [];
          const outboundShipment = shipmentResponse.data.getOutboundShipment;
          outboundShipment.shipmentLineItems.items.forEach((lineItem) => {
            lineItem.inventoryItems.items.forEach((inventoryItem) => {
              inventoryItem.inventory = lineItem.inventory;
              inventoryItems.push(inventoryItem);
            });
          });
          sortShipmentItemByLocation(inventoryItems, true);
          dispatch({
            type: "SET_CURRENT_SHIPPMENT",
            payload: { outboundShipment, inventoryItems },
          });

          const account = currentLineItem.inventory.account;

          if (
            shouldFetchInboundShipment({
              inboundShipmentId: currentLineItem.inventory.inboundShipmentId,
              lineItemInventoryItem,
              trackInboundSeals: account?.trackInboundSeals,
              verifyInboundRailCar: account?.verifyInboundRailCar,
            })
          ) {
            const inboundResponse = await API.graphql(
              graphqlOperation(getInboundShipmentSeals, {
                id: currentLineItem.inventory.inboundShipmentId,
              }),
            );
            const inboundShipment = inboundResponse.data.getInboundShipment;
            dispatch({
              type: "SET_INBOUND_SHIPMENT",
              payload: inboundShipment,
            });
          }
          dispatch({
            type: "SET_CURRENT_SHIPMENT_LINE_ITEM",
            payload: { currentLineItem, lineItemInventoryItem },
          });
        } else {
          setInvalidIdError(`Line item ID ${id} doesn't exist.`);
          return;
        }
        if (
          [LocationType.BULK, LocationType.BULK_MOBILE].includes(
            lineItemInventoryItem.inventoryItem.location.type,
          )
        ) {
          const aisle = await getLineItemAisle(
            lineItemInventoryItem.inventoryItem.location,
          );
          setBayList(aisle.locations.items);
        } else if (
          lineItemInventoryItem.inventoryItem.location.type ===
          LocationType.STRUCTURED
        ) {
          const aisle = await getLineItemAisle(
            lineItemInventoryItem.inventoryItem.location,
          );
          let bayList = [];
          for (let location of aisle.locations.items) {
            const bay = location.bay;
            if (!bayList[bay]) bayList[bay] = [];
            const level = location.level;
            if (!bayList[bay][level - 1]) bayList[bay][level - 1] = [];
            bayList[bay][level - 1].push(location);
          }
          bayList.splice(0, 1);
          for (let bay of bayList) {
            if (!bay || bay.length === 0) continue;
            for (let level of bay) {
              level.sort((a, b) => {
                return a.position - b.position;
              });
            }
          }
          setBayList(bayList);
        }
      } catch (err) {
        logError("InventoryItemCard:getLineItem", err, notify);
      } finally {
        if (showSpinner) {
          dispatch({ type: "SET_LOADING", payload: false });
        }
      }
    },
    [dispatch, notify],
  );

  const getLineItemAisle = async (location) => {
    const aisleResponse = await API.graphql(
      graphqlOperation(getAisle, { id: location.aisle.id }),
    );
    const aisle = aisleResponse.data.getAisle;
    if (aisle.locations.nextToken) {
      const locationResponse = await API.graphql(
        graphqlOperation(locationByAisleBySortKey, {
          aisleId: aisle.id,
          limit: 1000,
        }),
      );
      aisle.locations.items =
        locationResponse.data.locationByAisleBySortKey.items.filter(
          (itm) => itm.active === "true",
        );
    } else {
      aisle.locations.items = aisle.locations.items.filter(
        (itm) => itm.active === "true",
      );
    }
    return aisle;
  };

  const saveLineItem = async (lineItem) => {
    debugger;
    lineItem = { ...lineItem };
    delete lineItem.updatedAt;
    delete lineItem.createdAt;
    delete lineItem.inventory;
    delete lineItem.product;
    delete lineItem.inventoryItems;

    const response = await API.graphql(
      graphqlOperation(updateShipmentLineItem, { input: lineItem }),
    );
    dispatch({
      type: "UPDATE_CURRENT_INVENTORY",
      payload: response.data.updateShipmentLineItem,
    });
    debugger;
  };

  const setNextState = () => {
    if (lineItemInventoryItem.status === ShipmentLineItemStatus.PENDING) {
      lineItemInventoryItem.status = ShipmentLineItemStatus.LOADED;
    } else if (lineItemInventoryItem.status === ShipmentLineItemStatus.LOADED) {
      lineItemInventoryItem.status = ShipmentLineItemStatus.VERIFIED;
    } else if (
      lineItemInventoryItem.status === ShipmentLineItemStatus.REJECTED
    ) {
      lineItemInventoryItem.status = ShipmentLineItemStatus.LOADED;
    }
  };

  const handleSave = async (skipValidation = false, route = true) => {
    debugger;
    const previousStatus = currentLineItem.status;
    const expectedQuantity = lineItemInventoryItem?.quantityToPick ?? 0;
    if (
      !isLoaded &&
      expectedBarcode.length > 0 &&
      quantity !== expectedQuantity
    ) {
      notify(`Quantity Mismatch.`, "warning");
      return;
    }

    if (
      !isLoaded &&
      totalScanned !== totalExpected &&
      !lineItemInventoryItem?.hasException &&
      !skipValidation
    ) {
      setExceptionPromptOpen(true);
      return;
    }

    if (isContainerPickup && !isContainerNumberValidated) {
      notify("Validate container # to accept the shipment.", "warning");
      return;
    }
    if (
      trackRailcarVerificationType === "supplying" &&
      !isSourceRailcarValidated
    ) {
      notify(
        getShipmentProcessingBlockedMessage(trackRailcarVerificationType),
        "warning",
      );
      return;
    }
    if (
      trackRailcarVerificationType === "receiving" &&
      !isTargetRailcarValidated
    ) {
      notify(
        getShipmentProcessingBlockedMessage(trackRailcarVerificationType),
        "warning",
      );
      return;
    }
    if (inboundShipment?.seals?.items?.length > 0) {
      let invalidSeals = inboundShipment.seals.items.filter(
        (itm) =>
          itm.active &&
          itm.reValidated !== true &&
          itm.status !== SealStatus.NEW,
      );
      if (invalidSeals.length > 0) {
        notify("Validate all seals before processing the shipment.", "warning");
        return;
      }
      const response = await API.graphql(
        graphqlOperation(shipmentLineItemByInventoryForValidation, {
          inventoryId: currentLineItem?.inventoryId,
        }),
      );
      const pendingOutBounds =
        response?.data?.shipmentLineItemByInventory?.items?.filter(
          (item) =>
            item.status === ShipmentLineItemStatus.PENDING &&
            (item.outboundShipment.status === OutboundStatus.ARRIVED ||
              item.outboundShipment.status === OutboundStatus.PLACED) &&
            item.outboundShipment.id !== currentShipment?.id,
        );

      if (pendingOutBounds.length === 0) {
        invalidSeals = inboundShipment.seals.items.filter(
          (itm) => itm.active && itm.brokenBy,
        );
        const newSeals = inboundShipment.seals.items.filter(
          (itm) => itm.status === SealStatus.NEW,
        );
        if (invalidSeals.length === 0 && newSeals.length === 0) {
          notify(
            "Break/Replace old seals before processing the shipment.",
            "warning",
          );
          return;
        }
        if (invalidSeals.length > 0) {
          notify(
            "Replace broken seals before processing the shipment.",
            "warning",
          );
          return;
        }
      }
    }

    if (!skipValidation) {
      setNextState();
    }
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      if (note) {
        const newNote = {
          date: new Date(),
          by: window.appUser?.fullName,
          type: NoteType.GENERIC,
          text: note,
        };
        if (currentLineItem.notes) currentLineItem.notes.push(newNote);
        else currentLineItem.notes = [newNote];
        await saveLineItem(currentLineItem);
      }
      const promiseList = [];
      debugger;
      if (
        previousStatus === ShipmentLineItemStatus.PENDING &&
        lineItemInventoryItem.status === ShipmentLineItemStatus.LOADED
      ) {
        promiseList.push(setLocationCapacity());
        if (!currentShipment.startDateTime) {
          currentShipment.startDateTime = new Date().toISOString();
          promiseList.push(performUpdateOutbound(currentShipment));
        }
      }

      await Promise.all(promiseList);
      await saveLineItemInventoryItem();
      if (
        currentShipment.state === OutboundState.COMPLETED &&
        lineItemInventoryItem.status === ShipmentLineItemStatus.REJECTED
      ) {
        applyRejectionRollback(currentShipment);
        await saveShipment(currentShipment);
      }
      route &&
        history.replace("/processOutboundShipments/" + currentShipment.id);
    } catch (err) {
      logError(
        "processOutboundShipments:ShipmetItem:handleSave",
        err,
        notify,
        redirect,
      );
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const saveShipment = async (toUpdateShipment) => {
    const response = await performUpdateOutbound(toUpdateShipment);
    dispatch({
      type: "UPDATE_CURRENT_SHIPMENT",
      payload: response.data.updateOutboundShipment,
    });
    return response.data.updateOutboundShipment;
  };

  const saveLineItemInventoryItem = async () => {
    const lineItemInventoryItemInput = {
      ...lineItemInventoryItem,
      loadedBy: window.appUser?.fullName,
    };
    delete lineItemInventoryItemInput.updatedAt;
    delete lineItemInventoryItemInput.createdAt;
    delete lineItemInventoryItemInput.inventoryItem;
    const response = await API.graphql(
      graphqlOperation(updateShipmentLineItemInventoryItem, {
        input: lineItemInventoryItemInput,
      }),
    );
    const lineItemInventoryItemResponse =
      response.data.updateShipmentLineItemInventoryItem;
    dispatch({
      type: "SET_CURRENT_SHIPMENT_LINE_ITEM",
      payload: {
        currentLineItem,
        lineItemInventoryItem: lineItemInventoryItemResponse,
      },
    });
  };

  const initializeData = useCallback(async () => {
    try {
      const response = await API.graphql(
        graphqlOperation(barcodeByInventoryItemByActiveByValue, {
          inventoryItemId: lineItemInventoryItem?.inventoryItem?.id,
          active: "true",
        }),
      );
      const barcodes =
        response?.data?.barcodeByInventoryItemByActiveByValue?.items || [];
      setExpectedBarcode(barcodes);
    } catch (error) {
      logError("InventoryItemCard:getData", error, notify);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItemInventoryItem?.inventoryItem?.id]);

  useEffect(() => {
    if (lineItemInventoryItem?.inventoryItem?.id) {
      initializeData();
    }
  }, [
    lineItemInventoryItem?.inventoryItem?.id,
    lineItemInventoryItem?.status,
    dispatch,
    initializeData,
  ]);

  const isSealValidationRequired =
    inboundShipment?.sealNumbers?.length > 0 &&
    (currentLineItem?.inventory?.account?.trackInboundSeals ||
      currentLineItem?.inventory?.account?.trackInventorySeals);

  const actions = useMemo(() => {
    const actionConfigs = [
      {
        condition: expectedBarcode.length > 0,
        icon: <ExceptionIcon />,
        onClick: () => {
          setIsHasException(true);
          chooseBarcodeDamage?.current?.focus();
        },
        name: "Exceptions",
      },
      {
        condition: isSealValidationRequired,
        icon: <SealIcon />,
        onClick: () => {
          if (trackRailcarVerificationType === "supplying") {
            notify(
              getShipmentProcessingBlockedMessage(trackRailcarVerificationType),
              "warning",
            );
            return;
          }
          setScanSealOpen(true);
        },
        name: "Seal",
      },
      {
        condition: isContainerPickup,
        icon: <HashIcon />,
        onClick: () => setIsContainerNumberDialogOpen(true),
        name: "Container",
      },
    ];

    return actionConfigs.filter((action) => action.condition);
  }, [
    expectedBarcode.length,
    isContainerPickup,
    isSealValidationRequired,
    notify,
    trackRailcarVerificationType,
  ]);

  useEffect(() => {
    setBarcodeExceptionNote(lineItemInventoryItem?.barcodeExceptionNote);
    setIsHasException(lineItemInventoryItem?.hasException);
  }, [lineItemInventoryItem]);

  useEffect(() => {
    const resetState = getRailcarVerificationResetState();
    setSourceRailcarInput(resetState.sourceRailcarInput);
    setSourceRailcarError(resetState.sourceRailcarError);
    setTargetRailcarInput(resetState.targetRailcarInput);
    setTargetRailcarError(resetState.targetRailcarError);
    const hasProcessedShipment =
      lineItemInventoryItem?.status === ShipmentLineItemStatus.LOADED ||
      lineItemInventoryItem?.status === ShipmentLineItemStatus.VERIFIED;

    dispatch({
      type: "SET_SOURCE_RAILCAR_VALIDATED",
      payload: hasProcessedShipment,
    });
    dispatch({
      type: "SET_TARGET_RAILCAR_VALIDATED",
      payload: hasProcessedShipment,
    });
  }, [
    dispatch,
    inboundShipment?.id,
    lineItemInventoryItem?.id,
    lineItemInventoryItem?.status,
  ]);

  useEffect(() => {
    getData(match.params.id);
    return () => (isComponentMounted.current = false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useTrackCarSubscriptionRefresh({
    enabled: Boolean(currentShipment?.reloadYardId),
    label: "ShipmentItemOutbound",
    shouldRefresh: (trackCar) => {
      if (!trackCar?.reloadYardId) return false;
      if (trackCar.reloadYardId !== currentShipment.reloadYardId) return false;
      if (
        currentShipment?.id &&
        trackCar?.shipmentId &&
        trackCar.shipmentId !== currentShipment.id
      ) {
        return false;
      }
      return true;
    },
    onRefresh: (trackCar, eventName) => {
      if (
        eventName === "update" &&
        trackCar?.shipmentId === currentShipment?.id &&
        trackCar?.trackId
      ) {
        dispatch({
          type: "UPDATE_SHIPMENT_LOCATION",
          payload: {
            shipmentId: trackCar.shipmentId,
            trackId: trackCar.trackId,
            locationFullName: trackCar.locationFullName,
          },
        });
      }
      getData(match.params.id, { showSpinner: false });
    },
  });

  const handleScanButtonClick = async () => {
    await initializeData();
    setScanDialogOpen(true);
  };

  const validateContainerNumber = async () => {
    try {
      setIsContainerNumberValidated(true);
      setIsContainerNumberDialogOpen(false);
      dispatch({ type: "SET_LOADING", payload: true });
      lineItemInventoryItem.hasException = false;
      const updatedShipment = {
        ...currentShipment,
        hasException: false,
      };
      await Promise.all([
        saveShipment(updatedShipment),
        saveLineItemInventoryItem(),
      ]);
    } catch (error) {
      logError("InventoryItemCard:validateContainerNumber", error, notify);
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const handleScanClose = () => setScanDialogOpen(false);

  const handleScanBarcode = async (resultBarcodeList) => {
    setTotalScanned(resultBarcodeList);
    setQuantity(resultBarcodeList);
    setScanDialogOpen(false);
  };

  const notifyMessageByException = (exceptionValue) => {
    if (!!exceptionValue) {
      return notify("The barcode exception saved!");
    }
    return notify(
      "Select a reason for the barcode exception to proceed.",
      "warning",
    );
  };

  const handleCreateException = async (exceptionNote) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });

      // Update line item with exception details
      lineItemInventoryItem.hasException = true;
      lineItemInventoryItem.barcodeException = true;
      if (exceptionNote) {
        lineItemInventoryItem.barcodeExceptionNote = exceptionNote;
      } else {
        chooseBarcodeDamage?.current?.focus();
        lineItemInventoryItem.barcodeExceptionNote = "";
      }

      // Update shipment to indicate there's an exception
      const updatedShipment = {
        ...currentShipment,
        hasException: true,
      };

      // Perform save operations concurrently
      await Promise.all([
        saveShipment(updatedShipment),
        saveLineItemInventoryItem(),
      ]);
      // Notify the user of a successful update
      notifyMessageByException(exceptionNote || barcodeExceptions.MISMATCH);
    } catch (error) {
      // Unified error logging and user notification
      logError("InventoryItemCard:handleAddException", error, notify);
    } finally {
      // Ensure loading state is cleared
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const handleCreateExceptionContainerPickup = async (exceptionNote) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      lineItemInventoryItem.hasException = true;
      const updatedShipment = {
        ...currentShipment,
        hasException: true,
      };
      await Promise.all([
        saveShipment(updatedShipment),
        saveLineItemInventoryItem(),
      ]);
      notifyMessageByException(exceptionNote);
    } catch (error) {
      logError("InventoryItemCard:handleAddException", error, notify);
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
      setIsContainerNumberDialogOpen(false);
    }
  };

  const handleRemoveException = async () => {
    // If there's no exception to remove, return immediately
    if (
      !lineItemInventoryItem.hasException ||
      !lineItemInventoryItem.barcodeException
    ) {
      return;
    }

    try {
      dispatch({ type: "SET_LOADING", payload: true });

      // Reset exception details on the line item inventory
      lineItemInventoryItem.hasException = false;
      lineItemInventoryItem.barcodeException = false;
      lineItemInventoryItem.barcodeExceptionNote = "";

      // Check if the outbound shipment still has any exceptions
      const responseOutbound = await API.graphql(
        graphqlOperation(getOutboundShipmentMobile, {
          id: currentLineItem.outboundShipmentId,
        }),
      );
      const shipmentHasExceptions =
        responseOutbound?.data?.getOutboundShipment?.shipmentLineItems.items.some(
          (shipmentLineItem) =>
            shipmentLineItem.inventoryItems.items.some(
              (inventoryItem) =>
                (inventoryItem.barcodeException ||
                  inventoryItem.hasException) &&
                inventoryItem.id !== lineItemInventoryItem.id,
            ),
        );

      // Prepare operations to run concurrently
      const operations = [saveLineItemInventoryItem()];

      // If no exceptions remain on the shipment, update it
      if (!shipmentHasExceptions) {
        const updatedShipment = {
          ...currentShipment,
          hasException: false,
        };
        operations.push(saveShipment(updatedShipment));
      }

      // Perform all operations concurrently
      await Promise.all(operations);
    } catch (error) {
      // Log error and notify user
      logError("InventoryItemCard:handleRemoveException", error, notify);
    } finally {
      // Clear loading state regardless of success or error
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const onExceptionChoiceChange = (e) => {
    const value = e.target.value;
    if (value) {
      return handleCreateException(value);
    } else {
      return handleRemoveException();
    }
  };

  const setLocationCapacity = () => {
    debugger;
    const currentInventory = currentLineItem.inventory;
    const currentLocation = lineItemInventoryItem.inventoryItem.location;

    if (isUnmanagedLocation(currentLocation)) return Promise.resolve({});

    let capacityUsed = currentLocation.capacityUsed;
    if (isBulkLocation(currentLocation)) {
      capacityUsed =
        currentLocation.capacityUsed - lineItemInventoryItem.quantityToPick;
    } else {
      const inventoryLoadType =
        currentInventory.reloadYardCommodity.commodity.locationLoadType;
      // TODO
      // isnt't this handled by isUnmanagedLocation ???
      if (inventoryLoadType === LocationLoadType.OTHER) return;
      if (currentLocation.aisle.type === LocationType.STRUCTURED) {
        const locationLoadType =
          currentLocation.locationLoad[inventoryLoadType];
        if (locationLoadType.selected) {
          capacityUsed =
            currentLocation.capacityUsed - locationLoadType.capacity;

          if (capacityUsed === 0) {
            currentLocation.productId = null;
            currentLocation.productName = null;
          }
        }
      }
    }

    return promiseSetCapacityUsed(currentLocation, capacityUsed);
  };

  const handleCancel = () => {
    history.push("/processOutboundShipments/" + currentShipment.id);
  };

  const handleReject = () => {
    lineItemInventoryItem.status = ShipmentLineItemStatus.REJECTED;
    handleSave(true);
  };

  const getLocationAvatarClassname = (location) => {
    const invLocation = lineItemInventoryItem?.inventoryItem?.location;
    if (invLocation?.id && location.id === invLocation.id) {
      return classes.locationToPickAvatar;
    }
    return classes.locationAvatar;
  };

  const getTrackLocationView = () => {
    return (
      <>
        <Grid item xs={12}>
          <Box>
            <Divider />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TrackLocationView
            inventoryItem={lineItemInventoryItem.inventoryItem}
            selectedTrack={
              selectedTrackForVerification ??
              lineItemInventoryItem?.inventoryItem?.location
            }
            highlightShipmentId={trackHighlightShipmentId}
            hideSelectedRailcar={hideSelectedRailcar}
            sourceRailcarInput={
              trackRailcarVerificationType === "supplying"
                ? sourceRailcarInput
                : undefined
            }
            sourceRailcarError={
              trackRailcarVerificationType === "supplying"
                ? sourceRailcarError
                : false
            }
            onSourceRailcarInputChange={
              trackRailcarVerificationType === "supplying"
                ? (value) => {
                    setSourceRailcarInput(value);
                    setSourceRailcarError(false);
                    dispatch({
                      type: "SET_SOURCE_RAILCAR_VALIDATED",
                      payload: false,
                    });
                  }
                : undefined
            }
            onValidateSourceRailcar={
              trackRailcarVerificationType === "supplying"
                ? handleValidateSourceRailcar
                : undefined
            }
            targetRailcarInput={
              trackRailcarVerificationType === "receiving"
                ? targetRailcarInput
                : undefined
            }
            targetRailcarError={
              trackRailcarVerificationType === "receiving"
                ? targetRailcarError
                : false
            }
            onTargetRailcarInputChange={
              trackRailcarVerificationType === "receiving"
                ? (value) => {
                    setTargetRailcarInput(value);
                    setTargetRailcarError(false);
                    dispatch({
                      type: "SET_TARGET_RAILCAR_VALIDATED",
                      payload: false,
                    });
                  }
                : undefined
            }
            onValidateTargetRailcar={
              trackRailcarVerificationType === "receiving"
                ? handleValidateTargetRailcar
                : undefined
            }
          />
        </Grid>
      </>
    );
  };

  const getUnstructuredLocationView = () => {
    return (
      <>
        <Grid item xs={12}>
          <Box>
            <Divider />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Typography>
            {" "}
            Location:{" "}
            {lineItemInventoryItem.inventoryItem.locationFullName}{" "}
          </Typography>
        </Grid>
      </>
    );
  };

  const getBullLocationView = () => {
    return (
      bayList.length > 0 && (
        <div style={{ width: "100%" }}>
          <Grid item container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography component="span">{`Area: ${lineItemInventoryItem.inventoryItem.location.area?.name}`}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center">
                <Typography component="span">Zone:</Typography>
                <FiberManualRecordIcon
                  style={{
                    marginLeft: 2,
                    color:
                      lineItemInventoryItem.inventoryItem.location.zone?.color,
                  }}
                />
                <Typography component="span">
                  {lineItemInventoryItem.inventoryItem.location.zone?.name}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={6}>
              <Typography>{`Aisle: ${lineItemInventoryItem.inventoryItem.location.aisle.name}`}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            {
              <Grid item xs={12}>
                <Box mt={2} display="flex" justifyContent="center">
                  <div className={classes.bayContainer}>
                    <div className={classes.levelContainer}>
                      <Grid container spacing={1}>
                        {bayList.map((location) => (
                          <Grid id={location.id} item>
                            <div
                              key={location.id}
                              className={getLocationAvatarClassname(location)}
                            >
                              <Typography variant="body2">
                                {getLocationViewName(location)}
                              </Typography>
                            </div>
                          </Grid>
                        ))}
                      </Grid>
                    </div>
                  </div>
                </Box>
              </Grid>
            }
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="body2"
              style={{
                marginTop: 8,
                display: "flex",
                justifyContent: "center",
              }}
            >
              {inventoryItemLocationFullNameLabel}
            </Typography>
          </Grid>
        </div>
      )
    );
  };

  const getLocationView = () => {
    if (!lineItemInventoryItem) return null;
    const location = lineItemInventoryItem.inventoryItem.location;

    if (isTrack(location.area)) return getTrackLocationView();
    if (location.type === LocationType.UNSTRUCTURED)
      return getUnstructuredLocationView();

    if ([LocationType.BULK, LocationType.BULK_MOBILE].includes(location.type)) {
      return getBullLocationView();
    }

    const getCellSize = (list) => 12 / list.length;
    const bayIdx = location.bay - 1;
    const bay = bayList[bayIdx];
    const isFlat = !bay || (bay.length === 1 && bay[0].length === 1);

    return (
      <div style={{ width: "100%" }}>
        <Grid item container spacing={2}>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography component="span">{`Area: ${lineItemInventoryItem.inventoryItem.location.area?.name}`}</Typography>
          </Grid>
          {isMedium && (
            <Grid item xs={12}>
              {" "}
              <Divider />
            </Grid>
          )}
          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="center">
              <Typography component="span">Zone:</Typography>
              <FiberManualRecordIcon
                style={{
                  marginLeft: 2,
                  color:
                    lineItemInventoryItem.inventoryItem.location.zone?.color,
                }}
              />
              <Typography component="span">
                {lineItemInventoryItem.inventoryItem.location.zone?.name}
              </Typography>
            </Box>
          </Grid>
          {isMedium && (
            <Grid item xs={12}>
              {" "}
              <Divider />
            </Grid>
          )}
          <Grid item xs={6} md={4}>
            <Typography>{`Aisle: ${lineItemInventoryItem.inventoryItem.location.aisle.name}`}</Typography>
          </Grid>

          {!isMedium && (
            <Grid item xs={12}>
              {" "}
              <Divider />
            </Grid>
          )}
          <Grid item xs={6} md={4}>
            <Typography>
              {" "}
              {`Bay: ${lineItemInventoryItem.inventoryItem.location.bay}`}
            </Typography>
          </Grid>
          {isMedium && (
            <Grid item xs={12}>
              {" "}
              <Divider />
            </Grid>
          )}
          <Grid item xs={6} md={4}>
            <Typography>{`Position: ${lineItemInventoryItem.inventoryItem.location.position}`}</Typography>
          </Grid>
          <Grid item xs={6} md={4}>
            <Typography>{`Level: ${lineItemInventoryItem.inventoryItem.location.level}`}</Typography>
          </Grid>
          <Grid item xs={12}>
            {" "}
            <Divider />
          </Grid>
          {!isFlat && (
            <Grid item xs={12}>
              <div className={classes.location}>
                <div className={classes.bayContainer}>
                  <Box m={1}>
                    <Typography>{`Bay ${
                      (bayIdx + 1 < 10 ? " 0" : " ") + (bayIdx + 1)
                    }`}</Typography>
                  </Box>
                  <Divider className={classes.divider} />
                  {bay.map((level, idx) => (
                    <div key={idx} className={classes.levelContainer}>
                      <Grid container spacing={1}>
                        {level.map((location) => {
                          return (
                            <Grid id={location.id} item xs={getCellSize(level)}>
                              <div
                                key={location.id}
                                className={getLocationAvatarClassname(location)}
                              >
                                <Typography variant="body2">
                                  {getLocationViewName(location)}
                                </Typography>
                              </div>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </div>
                  ))}
                </div>
              </div>
            </Grid>
          )}
        </Grid>
        <Grid item xs={12}>
          <Typography
            variant="body2"
            style={{ marginTop: 8, display: "flex", justifyContent: "center" }}
          >
            {inventoryItemLocationFullNameLabel}
          </Typography>
        </Grid>
      </div>
    );
  };

  const handleManagerCancelClick = () => {
    debugger;
    if (lineItemInventoryItem.status === ShipmentLineItemStatus.PENDING)
      handleCancel();
    else handleReject();
  };

  const getCardActions = () => {
    if (!isAuthorized(AclType.OUTBOUND_VERIFY)) {
      return [
        <Button
          color="primary"
          size="large"
          disabled={
            lineItemInventoryItem?.status === ShipmentLineItemStatus.LOADED ||
            isSaveButtonDisabled()
          }
          onClick={() => handleSave(false, true)}
        >
          Mark Loaded
        </Button>,
        <Button onClick={handleCancel} size="large">
          Cancel
        </Button>,
      ];
    } else {
      return [
        <Button
          size="large"
          disabled={
            lineItemInventoryItem?.status === ShipmentLineItemStatus.REJECTED
          }
          onClick={handleManagerCancelClick}
        >
          {lineItemInventoryItem?.status === ShipmentLineItemStatus.PENDING
            ? "Cancel"
            : "Reject"}
        </Button>,
        <Button
          color="primary"
          size="large"
          disabled={isSaveButtonDisabled()}
          onClick={() =>
            handleSave(
              false,
              lineItemInventoryItem.status !== ShipmentLineItemStatus.REJECTED,
            )
          }
        >
          {lineItemInventoryItem?.status === ShipmentLineItemStatus.PENDING
            ? "Mark Loaded"
            : lineItemInventoryItem?.status === ShipmentLineItemStatus.REJECTED
              ? "Mark Loaded"
              : "Approve"}
        </Button>,
      ];
    }
  };

  if (invalidIdError) return <ErrorCard error={invalidIdError} />;

  if (needsOutboundRailcarSpotting(currentShipment)) {
    return (
      <CrewCard
        title="Pick List Disabled"
        subheader="Spot the railcar before accessing the pick list."
        action={
          <IconButton
            edge="start"
            onClick={() =>
              history.push("/processOutboundShipments/" + currentShipment.id)
            }
          >
            <HighlightOffIcon fontSize="large" />
          </IconButton>
        }
      >
        <CardContent>
          <Typography color="textSecondary" gutterBottom>
            Spot the railcar first, then return to complete the pick list.
          </Typography>
          <Button
            color="primary"
            variant="contained"
            onClick={() =>
              history.push("/processOutboundShipments/" + currentShipment.id)
            }
          >
            Back to Shipment
          </Button>
        </CardContent>
      </CrewCard>
    );
  }

  return (
    <div ref={imgRef}>
      <CrewCard
        title={currentLineItem?.inventory.productName}
        icon={<CheckBoxOutlineBlankOutlinedIcon fontSize="large" />}
        subheader={
          accountNameRenderer(currentLineItem?.inventory) +
          " - " +
          currentLineItem?.inventory.lotNumber +
          (isContainerPickup && pickupContainerNumber
            ? " - " + pickupContainerNumber
            : "")
        }
        action={[
          // <IconButton
          //     edge="start"
          //     onClick={handleCancel}
          // >
          //     <HelpOutlineOutlinedIcon fontSize="large" />
          // </IconButton>,
          <IconButton edge="start" onClick={handleCancel}>
            <HighlightOffIcon fontSize="large" />
          </IconButton>,
        ]}
        showDivider={false}
      >
        <CardContent>
          <Backdrop className={classes.backdrop} open={loading}>
            <CircularProgress color="inherit" />
          </Backdrop>
          <Grid container spacing={2}>
            <Grid item sm={12} xs={12}>
              <PhotoManager
                parentId={currentLineItem?.inventory.id}
                viewOnly={true}
              />
            </Grid>

            {showWeight(
              currentShipment.account,
              currentLineItem?.inventory,
            ) && (
              <Grid item sm={6} xs={12}>
                <Typography>
                  {`Weight: ${currentLineItem?.inventory.weight} ${currentLineItem?.inventory.weightUOM}/${currentLineItem?.inventory.inventoryUOM}`}
                </Typography>
              </Grid>
            )}
            {currentLineItem?.inventory.poNumber && (
              <Grid item sm={6} xs={12}>
                <Typography>
                  {`PO Number: ${currentLineItem?.inventory.poNumber}`}
                </Typography>
              </Grid>
            )}
            {currentLineItem?.inventory.lotNumber && (
              <Grid item sm={6} xs={12}>
                <Typography>
                  {`Lot Number: ${currentLineItem?.inventory.lotNumber}`}
                </Typography>
              </Grid>
            )}
            {isContainerPickup && pickupContainerNumber && (
              <Grid item sm={6} xs={12}>
                <Typography>
                  {`Container number: ${pickupContainerNumber}`}
                </Typography>
              </Grid>
            )}
            {currentLineItem?.inventory.productCode && (
              <Grid item sm={6} xs={12}>
                <Typography>
                  {`Product code: ${currentLineItem?.inventory.productCode}`}
                </Typography>
              </Grid>
            )}
            {(currentLineItem?.inventory?.pcs ||
              currentLineItem?.inventory?.product?.pcs) && (
              <Grid item sm={6} xs={12}>
                <Typography>
                  {`Pieces: ${
                    currentLineItem?.inventory.pcs ||
                    currentLineItem?.inventory?.product?.pcs
                  }`}
                </Typography>
              </Grid>
            )}

            {crewLocationRowVisible && (
              <Grid item sm={6} xs={12}>
                <Typography>
                  {`Location: ${crewLocationRowLabel ?? "NOT SPOTTED"}`}
                </Typography>
              </Grid>
            )}
            {currentLineItem?.inventory.customFields && (
              <Grid item xs={12}>
                <CustomFieldsMobile
                  recordFields={currentLineItem?.inventory.customFields}
                  accountFields={currentShipment.account?.inventoryCustomFields}
                  width={6}
                />
              </Grid>
            )}
            {lineItemInventoryItem?.loadedBy && (
              <Grid item xs={12}>
                <Typography>
                  {`Loaded By: ${lineItemInventoryItem.loadedBy}`}
                </Typography>
              </Grid>
            )}
            <Grid container spacing={2} item xs={12}>
              <Grid item sm={6} xs={12}>
                {expectedBarcode.length > 0 && !isLoaded ? (
                  <TextField
                    id="quantity-loaded"
                    label="Quantity"
                    variant="outlined"
                    fullWidth
                    size="small"
                    type="number"
                    required
                    error={isQuantityRequiredError}
                    helperText={
                      isQuantityRequiredError ? "Required" : undefined
                    }
                    value={quantity}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "") {
                        setQuantity("");
                      } else {
                        setQuantity(Number(v));
                      }
                    }}
                    inputProps={{ min: 1 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {`/ ${
                            lineItemInventoryItem?.quantityToPick ?? ""
                          } ${getUnitName(currentShipment?.totalUOM)}`}
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : (
                  <Typography>
                    {`Quantity: ${
                      lineItemInventoryItem?.quantityToPick ?? ""
                    } ${getUnitName(currentShipment?.totalUOM)}`}
                  </Typography>
                )}
              </Grid>
              {expectedBarcode.length > 0 && (
                <Grid item lg={3} md={6} sm={6} xs={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleScanButtonClick}
                    startIcon={<ScannerIcon />}
                  >
                    Scan
                  </Button>
                </Grid>
              )}
              {expectedBarcode.length > 0 && (
                <Grid item lg={3} md={6} sm={6} xs={6}>
                  <TextField
                    ref={chooseBarcodeDamage}
                    id="damage"
                    label="Scan Exception"
                    variant="outlined"
                    select
                    size="small"
                    fullWidth
                    error={!barcodeExceptionNote && isHasException}
                    helperText={
                      !barcodeExceptionNote &&
                      isHasException &&
                      "Choose Exception Option"
                    }
                    value={barcodeExceptionNote}
                    onChange={onExceptionChoiceChange}
                  >
                    {exceptionInputChoices.map((itm) => (
                      <MenuItem
                        selected={itm.value === barcodeExceptionNote}
                        key={itm.id}
                        value={itm.value}
                      >
                        {itm.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              <MissingScanDialog
                open={exceptionPromptOpen}
                onClose={() => setExceptionPromptOpen(false)}
                onConfirm={() => {
                  if (!barcodeExceptionNote) {
                    chooseBarcodeDamage?.current?.focus();
                    setExceptionPromptOpen(false);
                    setIsHasException(true);
                    return;
                  }
                }}
              />

              {isContainerNumberDialogOpen && (
                <ContainerNumberDialog
                  open={isContainerNumberDialogOpen}
                  containerNumber={pickupContainerNumber}
                  onValidationSuccess={validateContainerNumber}
                  onProceed={handleCreateExceptionContainerPickup}
                  close={() => setIsContainerNumberDialogOpen(false)}
                />
              )}
            </Grid>
            {scanDialogOpen && (
              <ExpectedBarcodeScanDialog
                open={scanDialogOpen}
                onClose={handleScanClose}
                onSave={handleScanBarcode}
                expectedBarcodes={expectedBarcode}
              />
            )}
            {isSealValidationRequired && (
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={trackRailcarVerificationType === "supplying"}
                  onClick={() => setScanSealOpen(true)}
                >
                  {isValidating ? "Validate Seals" : "Manage Seals"}
                </Button>
              </Grid>
            )}
            {scanSealOpen && (
              <ScanSealsDialog
                setBreakSealOpen={setBreakSealOpen}
                setApplySealOpen={setApplySealOpen}
                setDeactivatedSeal={setDeactivatedSeal}
                onClose={closeScanSealsDialog}
                revalidateSeal={revalidateSeal}
                inboundShipment={inboundShipment}
                dispatch={dispatch}
                loading={loading}
              />
            )}
            {breakSealOpen && (
              <BreakSealsDialog
                onClose={closeBreakSealsDialog}
                inboundShipment={inboundShipment}
                dispatch={dispatch}
                loading={loading}
                breakSealRequest={breakSeal}
              />
            )}
            {applySealOpen && (
              <ApplySealsDialog
                onClose={closeApplySealsDialog}
                saveSeal={createNewSeal}
                deActivateSeal={deActivateSeal}
                reActivateSeal={reActivateSeal}
                seal={deactivatedSeal}
                inboundShipment={inboundShipment}
                dispatch={dispatch}
                loading={loading}
              />
            )}
            <Grid item xs={12}>
              {getLocationView()}
            </Grid>
            {lineItemInventoryItem?.inventoryItem.location.aisle.type ===
              LocationType.STRUCTURED &&
            isCapacitySliderVisible(
              lineItemInventoryItem?.inventoryItem.location,
            ) ? (
              <Grid item xs={12}>
                <StructuredLocationCapacity
                  location={lineItemInventoryItem?.inventoryItem.location}
                />
              </Grid>
            ) : (
              isCapacitySliderVisible(
                lineItemInventoryItem?.inventoryItem.location,
              ) && (
                <Grid item xs={12}>
                  <Box ml={2} mt={-1}>
                    <LocationCapacity
                      aisle={
                        lineItemInventoryItem?.inventoryItem.location.aisle
                      }
                    />
                  </Box>
                </Grid>
              )
            )}
            <Grid item sm={12} xs={12}>
              <TextField
                name="notes"
                id="notes"
                label="Notes"
                multiline
                minRows={3}
                margin="dense"
                variant="outlined"
                fullWidth
                disabled={
                  lineItemInventoryItem?.status ===
                  ShipmentLineItemStatus.VERIFIED
                }
                onChange={(evt) => setNote(evt.target.value)}
              />
            </Grid>
            {/* <BottomContainers inventory={currentLineItem.inventory} /> */}
          </Grid>
          {actions.length > 0 && (
            <SpeedDialList
              classes={classes}
              handleSpeedDialClose={handleSpeedDialClose}
              handleSpeedDialOpen={handleSpeedDialOpen}
              isSpeedDialOpen={isSpeedDialOpen}
              actions={actions}
            />
          )}
        </CardContent>
        <CardActions className={classes.actions}>
          {getCardActions()}
        </CardActions>
      </CrewCard>
    </div>
  );
};

export default InventoryItemCard;
